import { useQuery } from "@tanstack/react-query";
import settingsApiService from "../../../services/api/SettingsApiService";
import { useAuthStore } from "../../zustand";

/**
 * Custom hook for fetching and caching user reminder settings
 *
 * Fetches both movement (standing) and exercise configuration settings.
 * This data is language-independent and contains user's reminder preferences.
 * Settings include: time windows, frequency, days of week, enabled status, and exercise preferences.
 *
 * Cache Strategy:
 * - Query key format: ['settings', email]
 * - Settings data is not language-specific (no language parameter needed)
 * - staleTime: 5 minutes (settings change moderately, less than homeData)
 * - gcTime: 10 minutes (keep in memory)
 * - Refetch on mount if stale, on network reconnect, but NOT on window focus
 *
 * Default Values (fallback):
 * - standConfig: 9am-5pm, 1hr frequency, no days selected, disabled
 * - excerciseConfig: 9am-5pm, 1hr frequency, no days selected, disabled, no physio
 *
 * @returns {Object} Query result object
 * @returns {Object} result.data - Settings data with standConfig and excerciseConfig
 * @returns {boolean} result.isLoading - True on first load (no cached data)
 * @returns {boolean} result.isFetching - True while any fetch is in progress
 * @returns {Error} result.error - Error object if fetch failed
 * @returns {Function} result.refetch - Manual refetch function
 */
export const useSettingsQuery = () => {
  // Get current user email from auth store (used as cache key dimension)
  const email = useAuthStore((state) => state.user.email);

  /**
   * TanStack Query hook for managing settings fetching and caching
   * Query is disabled until user is authenticated (email exists)
   */
  const query = useQuery({
    // Cache key: settings are per-user only (language-independent data)
    queryKey: ["settings", email],

    /**
     * Query function: fetch complete settings configuration for user
     * @async
     * @returns {Object} Structured settings with standConfig and excerciseConfig
     */
    queryFn: async () => {
      // Fetch complete user settings (movement and exercise configurations) from API
      const response = await settingsApiService.getCompleteSettings();

      // Return structured settings object with defaults for missing data
      // Defaults ensure UI always has valid config objects even if API data is incomplete
      return {
        // Movement (standing) reminder configuration
        standConfig: response.standConfig || {
          startTime: 9.0, // Start hour (24-hour format): 9am
          endTime: 17.0, // End hour (24-hour format): 5pm
          frequency: 1.0, // Frequency in hours between reminders
          daysOfWeek: [], // Array of day indices (0=Mon, 6=Sun) when reminders active
          isEnabled: false, // Whether movement reminders are enabled
          offset: null, // Offset for snooze functionality
        },
        // Exercise reminder configuration
        excerciseConfig: response.excerciseConfig || {
          startTime: 9.0, // Start hour (24-hour format): 9am
          endTime: 17.0, // End hour (24-hour format): 5pm
          frequency: 1.0, // Frequency in hours between reminders
          daysOfWeek: [], // Array of day indices (0=Mon, 6=Sun) when reminders active
          isEnabled: false, // Whether exercise reminders are enabled
          isPhysioEnabled: false, // Whether physiotherapy exercises are enabled
          preferences: [], // Array of selected exercise preference IDs
        },
      };
    },

    // Only enable query when user is authenticated (email available from auth store)
    enabled: !!email,

    // Data freshness: 5 minutes before marking as "stale" (settings change moderately)
    staleTime: 5 * 60 * 1000,
    // Garbage collection: keep cached data in memory for 10 minutes after last usage
    gcTime: 10 * 60 * 1000,
    // Refetch if stale when component mounts (user returns to settings screen)
    refetchOnMount: true,
    // Do NOT refetch when user switches browser tabs (reduces unnecessary API calls)
    refetchOnWindowFocus: false,
    // Refetch when network connectivity is restored (ensures fresh data)
    refetchOnReconnect: true,

    // Retry failed requests up to 2 times with exponential backoff (fewer retries than queries)
    // Backoff: 1s, 2s (up to 10s max)
    retry: 2,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 10000),
  });

  // Return subset of query state useful for UI components
  return {
    data: query.data, // Structured data: {standConfig, excerciseConfig} with defaults
    isLoading: query.isLoading, // True during initial load (no cached data exists)
    isFetching: query.isFetching, // True while any fetch is in progress (including background refetch)
    error: query.error, // Error object if fetch failed, null if successful
    refetch: query.refetch, // Function to manually trigger refetch
  };
};

